import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { delay, wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { MoneyApi } from "go-core/api/types";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { filterParseDateBetween } from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import ReportData from "go-report/reports";
import { reportParamsConverter } from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../services/Api/Admin/adminApi";
import ReportPartnerUtils from "../../services/ReportPartnerUtils";

interface State {
	resource?: Record<string, any>;
}

const listName = "ADMIN_PARTNER_REPORT";
const resourceType = "PARTNER_REPORT";
const getLastMonthFilterBetween = () => {
	const firstDate = new Date();
	firstDate.setHours(0);
	firstDate.setMinutes(0);
	firstDate.setSeconds(0);
	firstDate.setDate(1);

	const lastDate = new Date(firstDate.getFullYear(), firstDate.getMonth(), 0);
	lastDate.setHours(23);
	lastDate.setMinutes(59);
	lastDate.setSeconds(59);
	firstDate.setMonth(firstDate.getMonth() - 1);

	return filterParseDateBetween(firstDate, lastDate);
};
const Report = ({ resource }: State) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const configId = "admin_partner_report";

	if (!resource) return null;
	const data = resource.read();

	let config = {
		reportConfigId: configId,
		aggregatePrefix: "summary",
		selectedColumns: ReportPartnerUtils.getSelectedColumns(configId),
		groups: [...ReportPartnerUtils.getGroups(t)],
		showPercent: true,
		columns: [
			...ReportPartnerUtils.getColumns(t),
			{
				id: "provision_calculate_price",
				name: t("modules.partner_report.field.provision_calculate_price.title"),
				render: (item: MoneyApi) => {
					return `${FormatMoney(item)}`;
				},
			},
			{
				id: "sub_total_price",
				name: t("modules.partner_report.field.sub_total_price.title"),
				render: (item: MoneyApi) => {
					return `${FormatMoney(item)}`;
				},
			},
		],
		selectedGroups: ["PARTNER", "DATE"],
		groupsInfo: ReportPartnerUtils.getGroupsInfo(t),
		externalSelectedFilters: [
			{
				id: "date",
				filterType: "date",
				value: getLastMonthFilterBetween(),
				condition: "bt",
				visible: true,
			},
		],
		filters: [...ReportPartnerUtils.getFilters(t)],
		segments: [
			{
				id: "all",
				name: `${t("common.word.all", { ns: "lib" })}`,
				slug: "all",
				columns: ReportPartnerUtils.getSelectedColumns(configId),
				filters: [
					{
						filterId: "date",
						filterType: "date",
						value: getLastMonthFilterBetween(),
						condition: "bt",
					},
				],
				groups: [],
			},
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				adminApi.getAdminPartnerReport(reportParamsConverter(params), { cancelToken: sourceToken?.token }),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.partner_report.header.title"),
			filename: t("modules.partner_report.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const AdminPartnerReportPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const [showReport, setShowReport] = useState(true);
	const [summarizeLoading, setSummarizeLoading] = useState(false);
	const { t } = useTranslation("admin");
	const { addFlash, addSuccessFlash } = useFlash();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.partner_report.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	const onSummarizeHandler = async () => {
		setSummarizeLoading(true);
		try {
			await adminApi.summarizePartnersPayoffs();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await reloadReports();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setSummarizeLoading(false);
	};

	const reloadReports = async () => {
		setShowReport(false);
		await delay(10);
		setShowReport(true);
	};

	const buttons: ButtonProps[] = [
		{
			title: t("modules.partner_report.action.payoff.title"),
			loading: summarizeLoading,
			action: onSummarizeHandler,
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("modules.partner_report.action.payoff.title"),
			action: onSummarizeHandler,
		},
	];

	return (
		<div className={"content"}>
			{!isMobile && <Header title={t("modules.partner_report.header.title")} buttons={buttons} />}
			<MobileActions actions={mobileActions} />
			<Suspense fallback={<LoadingContainer />}>{showReport && <Report resource={resource} />}</Suspense>
		</div>
	);
};

export default AdminPartnerReportPage;
